import * as React from 'react';

import ActionButton from 'components/buttons/action-button/ActionButton';
import InputRadio from 'components/inputs/input-radio/InputRadio';
import Spinner from 'components/spinner/Spinner';
import { ModalFrame, ModalFrameFooter, ModalFrameInner } from 'components/modal';
import { Form, FormField } from 'components/form';

import getInstagramName from 'modules/misc/utils/get-instagram-name';
import { __t, applyTranslations } from 'utils/language';

import './realm-profile-match-modal.scss';

applyTranslations(require('./realm-profile-match-modal.i18n.json'));
applyTranslations(require('../realm-outfit-review/realm-outfit-review.i18n.json'));

import {
  RealmProfileEMSResource
} from '../../types/RealmProfile.type';

interface OwnProps {
  matches: Array<RealmProfileEMSResource>,
  onSubmit: (emsId: string) => Promise<boolean>,
  onCloseModal: () => void
}

type Props = OwnProps;

const RealmProfileMatchModal = ({
  matches,
  onSubmit,
  onCloseModal
}: Props) => {
  const [emsId, setEmsId] = React.useState<null | string>(null);
  const [inProgress, setInProgress] = React.useState<boolean>(false);
  const baseEMSURL = window.app.env.IS_PROD
    ? 'https://creator-content-platform.dx-ie.zalan.do/creator/edit'
    : 'https://creator-content-platform.dx-ie-test.zalan.do/creator/edit';

  return (
    <ModalFrame
      title={__t('realm_profile_match_modal.title')}
      subtitle={__t('realm_profile_match_modal.subtitle')}
    >
      <ModalFrameInner
        narrowContent
      >
        <Form
          onSubmit={async () => {
            if (emsId === null || inProgress) {
              return;
            }

            setInProgress(true);

            const success = await onSubmit(emsId);

            setInProgress(false);

            if (success) {
              onCloseModal();
            }
          }}
        >
          <FormField
            direction="column"
            label={__t('realm_profile_match_modal.field_label')}
            labelType="highlighted"
            showValidationIcon={false}
            className="realm-profile-match-modal__options"
          >
            {matches.map((match, key) => (
              <div
                key={key}
                className="realm-profile-match-modal__option"
              >
                <div className="realm-profile-match-modal__option-radio">
                  <InputRadio
                    checked={match.cmsCreatorProfileUuid === emsId}
                    id={`realm-profile-match-modal-radio-${key}`}
                    label={undefined}
                    value={match.cmsCreatorProfileUuid}
                    onChange={() => setEmsId(match.cmsCreatorProfileUuid)}
                  />
                </div>
                <div
                  className="realm-profile-match-modal__option-content"
                  onClick={() => setEmsId(match.cmsCreatorProfileUuid)}
                >
                  <div className="realm-profile-match-modal__option-content-line">
                    <span className="realm-profile-match-modal__content-label">
                      {__t('realm_profile_match_modal.option.creator_info')}
                    </span>
                    <span className="realm-profile-match-modal__content-name">
                      {match.name}
                    </span>
                    {match.socialProfiles ? (
                      <span>
                        <a
                          target="_blank"
                          rel="noreferrer"
                          className="realm-profile-match-modal__content-link"
                          href={match.socialProfiles.instagram}
                        >
                          @{getInstagramName(match.socialProfiles.instagram)}
                        </a>
                      </span>
                    ) : null}
                  </div>
                  <div className="realm-profile-match-modal__option-content-line">
                    <span className="realm-profile-match-modal__content-label">
                      {__t('realm_profile_match_modal.option.id')}
                    </span>
                    <span>
                      <a
                        href={`${baseEMSURL}/${match.cmsCreatorProfileUuid}`}
                        target="_blank"
                        rel="noreferrer"
                        className="realm-profile-match-modal__content-link"
                      >
                        {match.cmsCreatorProfileUuid}
                      </a>
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </FormField>
          <ModalFrameFooter>
            <ActionButton
              color="tertiary"
              onClick={onCloseModal}
            >
              {__t('collabary_common.action_button.cancel')}
            </ActionButton>
            <ActionButton
              disabled={emsId === null || inProgress}
              type="submit"
              color="secondary"
            >
              {__t('realm_profile_match_modal.match_button')}
              {inProgress ? (
                <Spinner />
              ) : null}
            </ActionButton>
          </ModalFrameFooter>
        </Form>
      </ModalFrameInner>
    </ModalFrame>
  );
};

export default RealmProfileMatchModal;
